import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { ActivatedRoute, Router } from '@angular/router';
import { NGXLogger } from 'ngx-logger';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { Api } from 'src/app/shared/constants/api';
import { PermissionKey } from 'src/app/shared/constants/permissionCode';
import { IPermissionWithShow } from 'src/app/shared/interface';
import { DataService } from 'src/app/shared/services/data.service';
import { PermissionService } from 'src/app/shared/services/permissions.service';
import { UtilityService } from 'src/app/shared/services/utility.service';
import { IExternalEntity, IExternalEntityCriteria } from '../external-entity.interface';
import { BranchContactComponent } from '../branch-contact/branch-contact.component';
import * as _ from 'underscore';
import { Subscription } from 'rxjs';
import { Message } from 'src/app/shared/constants/message';

const FILTER_STORAGE_KEY = 'entity_list_filter_state';

@Component({
    selector: 'app-entity-lis',
    templateUrl: './entity-list.component.html',
    styleUrls: ['./entity-list.component.less'],
    standalone: false
})
export class EntityListComponent implements OnInit, AfterViewInit, OnDestroy {

  constructor(private formBuilder: FormBuilder, private logger: NGXLogger,
    private service: DataService, private utilityService: UtilityService,
    private activatedRoute: ActivatedRoute, private router: Router,
    private permissionService: PermissionService, private dialog: MatDialog) { }

  entityListForm: FormGroup;
  entityListDataSource: IExternalEntity[] = [];
  criteria: IExternalEntityCriteria = {};
  @ViewChild(MatSort) sort: MatSort;
  @ViewChild(MatPaginator) paginator: MatPaginator;
  pageSizeOptions = this.utilityService.getPageSizeOptions();
  // Bound to <mat-paginator [pageSize]>. Without this binding the paginator
  // has no persisted starting size to read on (re)load, so navigating away
  // (e.g. opening an entity) and back always reset it to the paginator's
  // own default instead of whatever the user had last chosen.
  pageSize: number = this.pageSizeOptions[0];
  pageIndex: number = 0;
  totalRecords: any;
  // Incremented on every getExternalEntityList() call; a response is only
  // applied if this hasn't moved on since — otherwise an earlier, slower
  // request (e.g. from a page-size change immediately followed by another)
  // could resolve after a newer one and overwrite it with stale data/count,
  // which is what made a newly-selected page size still show the old
  // number of rows.
  private requestSeq = 0;
  entityColumns = ['expand', 'code', 'name', 'city', 'country', 'numberOfBranch', 'status', 'action'];
  addPermission: boolean = false;
  viewPermission: boolean = false;
  editPermission: boolean = false;
  branchViewPermission: boolean = false;
  /** General permission — controls button visibility and Add/Edit access in contact dialog */
  branchContactPermission: boolean = false;
  subscription = new Subscription();

  expandedEntityId: number | null = null;
  branchesMap = new Map<number, any[]>();
  branchLoadingSet = new Set<number>();

  private readonly entityTypeLabels: Record<string, string> = {
    EXPORTER: 'Exporter', IMPORTER: 'Importer', OVERSEAAGENT: 'Oversea Agent',
    IATA: 'IATA Agent', FORWARDER: 'Forwarder', CONSOLIDATOR: 'Consolidator/Coloader',
    CUSTOMSBROKER: 'Customs Broker', TRANSPORTERLSP: 'Transporter-LSP',
    LINE: 'Line / NVOCC', SHIPPINGLINEAGENT: 'Shipping Line Agent',
    TRANSPORTERGTA: 'Transporter-GTA', CFSWAREHOUSE: 'CFS / Warehouse',
    SURVEYOR: 'Surveyor', TRAINOPERATOR: 'Train Operator', CFS: 'CFS'
  };

  ngOnInit(): void {
    this.intializeForm();
    this.getAllPermissions();
    this.restoreFiltersFromStorage();
    this.setCodeValueChangeEvent();
    this.setNameValueChangeEvent();
    this.setCityValueChangeEvent();
  }

  ngAfterViewInit(): void {
    // Explicitly push the restored page size into the paginator so the first
    // API call uses the correct size even if Angular's [pageSize] binding
    // hasn't propagated yet due to change-detection timing.
    this.paginator.pageSize = this.pageSize;
    this.paginator.pageIndex = this.pageIndex;

    const paginatorSub = this.paginator.page.subscribe(event => {
      // Keep pageSize and pageIndex in sync with the user's interactive
      // selection so saveFiltersToStorage() always has the current values.
      this.pageSize = event.pageSize;
      this.pageIndex = event.pageIndex;
      this.getExternalEntityList();
    });
    this.subscription.add(paginatorSub);

    this.getExternalEntityList();
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
    this.saveFiltersToStorage();
  }

  intializeForm() {
    this.entityListForm = this.formBuilder.group({
      code: [],
      name: [],
      city: [],
    });
  }

  sortData(event: any) {
    this.criteria.sort = [event.active, event.direction];
    this.getExternalEntityList(true);
  }

  setCodeValueChangeEvent() {
    const sub = this.code.valueChanges.pipe(debounceTime(200), distinctUntilChanged()).subscribe(val => {
      this.criteria.code = val?.trim().toLowerCase();
      this.getExternalEntityList(true);
    });
    this.subscription.add(sub);
  }

  setNameValueChangeEvent() {
    const sub = this.name.valueChanges.pipe(debounceTime(200), distinctUntilChanged()).subscribe(val => {
      this.criteria.name = val?.trim().toLowerCase();
      this.getExternalEntityList(true);
    });
    this.subscription.add(sub);
  }

  setCityValueChangeEvent() {
    const sub = this.city.valueChanges.pipe(debounceTime(200), distinctUntilChanged()).subscribe(val => {
      this.criteria.city = val?.trim().toLowerCase();
      this.getExternalEntityList(true);
    });
    this.subscription.add(sub);
  }

  toggleExpand(entity: IExternalEntity, event: Event): void {
    event.stopPropagation();
    if (this.expandedEntityId === entity.id) {
      this.expandedEntityId = null;
    } else {
      this.expandedEntityId = entity.id;
      this.branchesMap.delete(entity.id);
      this.loadBranches(entity.id);
    }
  }

  loadBranches(entityId: number): void {
    if (this.branchesMap.has(entityId)) return;
    this.branchLoadingSet.add(entityId);
    this.service.get(Api.ENTITY_BRANCH_LIST, { entityIds: [entityId], size: 100 }).subscribe(res => {
      this.branchLoadingSet.delete(entityId);
      const branches = res || [];
      this.branchesMap.set(entityId, branches);
      const entity = this.entityListDataSource.find(e => e.id === entityId);
      if (entity) entity.numberOfBranch = branches.length;
    });
  }

  getBranches(entityId: number): any[] {
    return this.branchesMap.get(entityId) || [];
  }

  isBranchLoading(entityId: number): boolean {
    return this.branchLoadingSet.has(entityId);
  }

  getStatusLabel(element: any): string {
    const approvalMsg = this.utilityService.getApprovalMessage({
      status: element.status,
      submittedForApproval: element.submittedForApproval,
      deactivationWefDate: element.deactivateDtls?.deactivationWefDate,
      reactivationWefDate: element.reactivateDtls?.reactivationWefDate
    });
    if (approvalMsg) { return approvalMsg; }
    switch (element.status) {
      case 'DRAFT':       return 'Save as Draft';
      case 'PENDING':     return 'Pending Approval';
      case 'APPROVED':    return 'Approved';
      case 'DEACTIVATED': return 'Deactivated';
      default:            return element.status || '-';
    }
  }

  getStatusClass(status: string): string {
    switch (status) {
      case 'DRAFT':       return 'status-draft';
      case 'PENDING':     return 'status-pending';
      case 'APPROVED':    return 'status-approved';
      case 'DEACTIVATED': return 'status-deactivated';
      default:            return '';
    }
  }

  getBranchAddress(branch: any): string {
    const addr = branch.address;
    if (!addr) return '';
    const city = addr.cityName || '';
    const pin = addr.pincode || '';
    return city && pin ? `${city} - ${pin}` : city || pin;
  }

  openEntityForm(id?: number) {
    if (id) {
      this.editPermission
        ? this.router.navigate(['edit/' + id], { relativeTo: this.activatedRoute, queryParams: { location: 'edit' } })
        : this.utilityService.notifier(Message.EDIT_PERMISSION_WARNING, 'warning');
    } else {
      this.addPermission
        ? this.router.navigate(['add'], { relativeTo: this.activatedRoute })
        : this.utilityService.notifier(Message.ADD_PERMISSION_WARNING, 'warning');
    }
  }

  openEntityView(id: number) {
    this.viewPermission
      ? this.router.navigate(['view/' + id], { relativeTo: this.activatedRoute, queryParams: { location: 'view' } })
      : this.utilityService.notifier(Message.VIEW_PERMISSION_WARNING, 'warning');
  }

  openBranchContactDialog(branch: any, entity?: any): void {
    const isDeactivated = branch.status === 'DEACTIVATED' || entity?.status === 'DEACTIVATED';
    this.dialog.open(BranchContactComponent, {
      data: {
        branchId: branch.id,
        buttonHidden: isDeactivated || !this.branchContactPermission,
        disableTable: isDeactivated || !this.branchContactPermission,
        isDialogMode: !isDeactivated,
        showCloseButton: true
      },
      maxWidth: '90vw',
      maxHeight: '80vh'
    });
  }

  getExternalEntityList(resetPageIndex: boolean = false) {
    if (resetPageIndex) {
      this.paginator.pageIndex = 0;
      this.pageIndex = 0;
      this.expandedEntityId = null;
    }
    if (this.viewPermission) {
      this.criteria.sort = this.criteria.sort ? this.criteria.sort : ['createdDate', 'desc'];
      this.criteria.page = this.paginator?.pageIndex;
      this.criteria.size = this.paginator?.pageSize;
      const seq = ++this.requestSeq;
      this.service.get(Api.EXTERNAL_ENTITY_NAMES, this.criteria).subscribe(res => {
        // Discard this response if a newer request has since been fired
        // (e.g. the page-size dropdown changing twice in quick succession) —
        // otherwise an older, slower response can arrive last and overwrite
        // the table with a stale page size/record count.
        if (seq !== this.requestSeq) { return; }
        if (res) {
          if (res.contents.length === 0) {
            this.utilityService.notifier(Message.NO_RECORDS_FOUND, 'info');
          }
          res.contents.forEach(entity => {
            entity.city = entity.address?.cityName || '';
            entity.countryName = entity.address?.countryName || '';
            entity.entityType = Array.isArray(entity.entityDetails)
              ? entity.entityDetails.map(k => this.entityTypeLabels[k] || k).join(', ')
              : '';
          });
          this.logger.info('Entity list response', res.contents);
          this.totalRecords = res.totalRecords;
          this.entityListDataSource = res.contents;
          this.branchesMap.clear();
          if (this.paginator.pageIndex >= Math.ceil(this.totalRecords / this.paginator.pageSize)) {
            this.paginator.pageIndex = 0;
          }
        }
      });
    } else {
      this.utilityService.notifier(Message.VIEW_PERMISSION_WARNING, 'warning');
    }
  }

  private saveFiltersToStorage(): void {
    const state = {
      code: this.criteria.code || null,
      name: this.criteria.name || null,
      city: this.criteria.city || null,
      pageSize: this.pageSize,
      pageIndex: this.pageIndex,
    };
    sessionStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(state));
  }

  private restoreFiltersFromStorage(): void {
    const saved = sessionStorage.getItem(FILTER_STORAGE_KEY);
    if (!saved) return;
    try {
      const state = JSON.parse(saved);
      this.criteria.code = state.code;
      this.criteria.name = state.name;
      this.criteria.city = state.city;
      if (state.pageSize) { this.pageSize = state.pageSize; }
      if (state.pageIndex) { this.pageIndex = state.pageIndex; }
      this.entityListForm.patchValue({ code: state.code, name: state.name, city: state.city }, { emitEvent: false });
    } catch (_) {}
  }

  public getAllPermissions() {
    const entityPermissions: IPermissionWithShow[] = this.permissionService.checkPermissions([
      PermissionKey.EXTERNAL_ENTITY_ADD_PERMISSION,
      PermissionKey.EXTERNAL_ENTITY_VIEW_PERMISSION,
      PermissionKey.EXTERNAL_ENTITY_EDIT_PERMISSION,
      PermissionKey.ENTITY_BRANCH_CONTACT_PERMISSION,
    ], PermissionKey.ENTITY_MODULE);

    const branchPermissions: IPermissionWithShow[] = this.permissionService.checkPermissions(
      [PermissionKey.ENTITY_BRANCH_VIEW_PERMISSION], PermissionKey.ENTITY_BRANCH_MODULE
    );

    this.addPermission = _.findWhere(entityPermissions, { permissionCode: PermissionKey.EXTERNAL_ENTITY_ADD_PERMISSION })?.show ? true : false;
    this.viewPermission = _.findWhere(entityPermissions, { permissionCode: PermissionKey.EXTERNAL_ENTITY_VIEW_PERMISSION })?.show ? true : false;
    this.editPermission = _.findWhere(entityPermissions, { permissionCode: PermissionKey.EXTERNAL_ENTITY_EDIT_PERMISSION })?.show ? true : false;
    this.branchContactPermission = _.findWhere(entityPermissions, { permissionCode: PermissionKey.ENTITY_BRANCH_CONTACT_PERMISSION })?.show ? true : false;
    this.branchViewPermission = _.findWhere(branchPermissions, { permissionCode: PermissionKey.ENTITY_BRANCH_VIEW_PERMISSION })?.show ? true : false;
  }

  get code() { return this.entityListForm.get('code'); }
  get name() { return this.entityListForm.get('name'); }
  get city() { return this.entityListForm.get('city'); }
}
